/* eslint-disable */
<template>
  <div
    :class="[
      'min-h-screen transition-colors duration-300 font-[\'Inter\',sans-serif]',
      isLight ? 'bg-[#e4f4f0]' : 'bg-[#0f1f1c]',
    ]"
  >
    <NavBarComponent :isLight="isLight" />

    <!-- HERO / SEARCH HEADER -->
    <section class="relative overflow-hidden pt-32 pb-12 px-10">
      <!-- Radial glow -->
      <div
        class="absolute -top-20 left-[40%] w-[700px] h-[500px] pointer-events-none"
        :class="
          isLight
            ? 'bg-[radial-gradient(ellipse,rgba(26,155,138,0.14)_0%,transparent_65%)]'
            : 'bg-[radial-gradient(ellipse,rgba(26,155,138,0.08)_0%,transparent_65%)]'
        "
      ></div>

      <!-- Imigongo SVG patterns -->
      <div
        class="absolute inset-0 overflow-hidden pointer-events-none"
        aria-hidden="true"
      >
        <!-- top-right: large diamond grid -->
        <svg
          class="absolute -top-10 -right-10 w-[420px] h-[420px] pointer-events-none"
          :style="{ opacity: isLight ? 0.12 : 0.1 }"
          viewBox="0 0 160 160"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
            <rect
              x="40"
              y="0"
              width="28"
              height="28"
              transform="rotate(45 40  0)"
            />
            <rect
              x="80"
              y="0"
              width="28"
              height="28"
              transform="rotate(45 80  0)"
            />
            <rect
              x="120"
              y="0"
              width="28"
              height="28"
              transform="rotate(45 120 0)"
            />
            <rect
              x="20"
              y="40"
              width="28"
              height="28"
              transform="rotate(45 20  40)"
            />
            <rect
              x="60"
              y="40"
              width="28"
              height="28"
              transform="rotate(45 60  40)"
            />
            <rect
              x="100"
              y="40"
              width="28"
              height="28"
              transform="rotate(45 100 40)"
            />
            <rect
              x="140"
              y="40"
              width="28"
              height="28"
              transform="rotate(45 140 40)"
            />
            <rect
              x="40"
              y="80"
              width="28"
              height="28"
              transform="rotate(45 40  80)"
            />
            <rect
              x="80"
              y="80"
              width="28"
              height="28"
              transform="rotate(45 80  80)"
            />
            <rect
              x="120"
              y="80"
              width="28"
              height="28"
              transform="rotate(45 120 80)"
            />
            <rect
              x="20"
              y="120"
              width="20"
              height="20"
              transform="rotate(45 20  120)"
            />
            <rect
              x="60"
              y="120"
              width="20"
              height="20"
              transform="rotate(45 60  120)"
            />
            <rect
              x="100"
              y="120"
              width="20"
              height="20"
              transform="rotate(45 100 120)"
            />
            <rect
              x="140"
              y="120"
              width="20"
              height="20"
              transform="rotate(45 140 120)"
            />
          </g>
        </svg>

        <!-- bottom-left: concentric squares -->
        <svg
          class="absolute -bottom-10 -left-10 w-[280px] h-[280px] pointer-events-none"
          :style="{ opacity: isLight ? 0.09 : 0.08 }"
          viewBox="0 0 120 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g
            :stroke="isLight ? '#0f1f1c' : '#a8d5cc'"
            stroke-width="3"
            fill="none"
          >
            <rect
              x="60"
              y="10"
              width="64"
              height="64"
              transform="rotate(45 60 10)"
            />
            <rect
              x="60"
              y="22"
              width="46"
              height="46"
              transform="rotate(45 60 22)"
            />
            <rect
              x="60"
              y="34"
              width="28"
              height="28"
              transform="rotate(45 60 34)"
            />
            <rect
              x="60"
              y="46"
              width="10"
              height="10"
              transform="rotate(45 60 46)"
            />
          </g>
        </svg>
      </div>

      <!-- Pill badge -->
      <div
        class="relative z-10 inline-flex items-center gap-2 mb-6 text-[11px] font-semibold tracking-widest uppercase border rounded-full px-4 py-1.5"
        :class="
          isLight
            ? 'text-[#0d6b5e] bg-[#1a9b8a]/10 border-[#1a9b8a]/30'
            : 'text-[#a8d5cc] bg-[#1a9b8a]/10 border-[#1a9b8a]/25'
        "
      >
        <span
          class="w-1.5 h-1.5 rounded-full animate-pulseFast bg-[#1a9b8a]"
        ></span>
        ✦ {{ $t('searchResults.badge') }}
      </div>

      <!-- Headline -->
      <h1
        class="relative z-10 font-['Montserrat'] font-extrabold leading-[1.04] tracking-[-2px] max-w-1xl mb-8"
        :class="[
          'text-[clamp(34px,5vw,66px)]',
          isLight ? 'text-[#0f1f1c]' : 'text-white',
        ]"
      >
        <template v-if="searchTerm">
          {{ $t('searchResults.resultsFor') }}<br />
          <em
            class="not-italic"
            :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
            >"{{ searchTerm }}"</em
          >
        </template>
        <template v-else>
          {{ $t('searchResults.findNext') }}<br />
          <em
            class="not-italic"
            :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
            >{{ $t('searchResults.experience') }}</em
          >
        </template>
      </h1>

      <!-- Search -->
      <div class="relative z-10">
        <SearchComponent :isLight="isLight" />
      </div>
    </section>

    <!-- Results section -->
    <section class="px-10 pb-24">
      <!-- Loading state -->
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center py-28 gap-5"
      >
        <div
          class="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
          :class="isLight ? 'border-[#1a9b8a]' : 'border-[#a8d5cc]'"
        ></div>
        <p
          class="text-[11px] font-semibold tracking-widest uppercase"
          :class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'"
        >
          {{ $t('searchResults.loading') }}
        </p>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="events.length === 0"
        class="flex flex-col items-center justify-center py-28 gap-5"
      >
        <svg
          class="w-14 h-14"
          :style="{ opacity: isLight ? 0.25 : 0.2 }"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="32"
            y="2"
            width="38"
            height="38"
            transform="rotate(45 32 2)"
            :fill="isLight ? '#0d6b5e' : '#a8d5cc'"
          />
          <rect
            x="32"
            y="14"
            width="22"
            height="22"
            transform="rotate(45 32 14)"
            :fill="isLight ? '#e4f4f0' : '#0f1f1c'"
          />
          <rect
            x="32"
            y="24"
            width="8"
            height="8"
            transform="rotate(45 32 24)"
            :fill="isLight ? '#0d6b5e' : '#a8d5cc'"
          />
        </svg>
        <p
          class="font-['Montserrat'] font-extrabold text-2xl tracking-tight"
          :class="isLight ? 'text-[#0f1f1c]' : 'text-white'"
        >
          {{ $t('searchResults.nothingTitle') }}
        </p>
        <p
          class="text-sm max-w-xs text-center leading-relaxed"
          :class="isLight ? 'text-[#0f1f1c]/50' : 'text-white/40'"
        >
          {{ $t('searchResults.nothingFound') }}
          <span :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
            >"{{ searchTerm }}"</span
          >{{ $t('searchResults.tryAnother') }}
        </p>
      </div>

      <!-- Results -->
      <template v-else>
        <!-- Section header — mirrors EventsGridComponent -->
        <div class="flex items-baseline justify-between mb-6">
          <h2
            class="font-display font-bold text-[19px] tracking-tight"
            :class="isLight ? 'text-[#0f1f1c]' : 'text-white'"
          >
            {{ $t('searchResults.eventsFound') }}
            <span
              class="text-xs font-normal ml-2"
              :class="isLight ? 'text-[#6b8f89]' : 'text-[#a8d5cc]/60'"
            >
              {{ displayedEvents.length }} {{ displayedEvents.length !== 1 ? $t('searchResults.results') : $t('searchResults.result') }}
            </span>
          </h2>
        </div>

        <!-- Cards grid — same auto-fill pattern as EventsGridComponent -->
        <div
          class="grid gap-3.5"
          style="grid-template-columns: repeat(auto-fill, minmax(185px, 1fr))"
        >
          <EventCardComponent
            v-for="event in displayedEvents"
            :key="event.id"
            :event="event"
            :isLight="isLight"
            @click="selectedEvent = event"
          />
        </div>
      </template>
    </section>

    <!-- Event detail modal -->
    <MapEventModelComponent
      v-if="selectedEvent"
      :showModal="!!selectedEvent"
      :selectedEvent="selectedEvent"
      :isLight="isLight"
      @close="selectedEvent = null"
    />

    <!-- Footer -->
    <FooterComponent :isLight="isLight" @toggle-mode="toggleMode" />
  </div>
</template>

<script>
import apiService from "@/services/api";
import NavBarComponent from "@/components/NavBarComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import EventCardComponent from "@/components/EventCardComponent.vue";
import MapEventModelComponent from "@/components/MapEventModelComponent.vue";
import { useTheme } from "@/composables/useTheme";


export default {
  components: {
    NavBarComponent,
    FooterComponent,
    SearchComponent,
    EventCardComponent,
    MapEventModelComponent,
  },
setup() {
        const { isLight, toggleMode } = useTheme();
        return { isLight, toggleMode };
    },
  data() {
    return {
      selectedEvent: null,
      searchTerm: this.$route.query.event || "",
      events: [],
      loading: false,
      debug: false,
    };
  },

  computed: {
    displayedEvents() {
      return this.events.map((item) => {
        const statusObj = this.getEventStatus(item);

        const statusMap = {
          LIVE: "live",
          "STARTING SOON": "soon",
          RSVP: "rsvp",
          ENDED: "ended",
        };

        return {
          id: item.event_id,
          Title: item.title,
          ImageURL: item.image_url || null,
          posterGhost: item.title?.toUpperCase() || "",
          date: item.date,
          venue: item.organizer || item.location,
          location: item.location,
          Description: item.description,
          tags: Array.isArray(item.categories)
            ? item.categories.map((c) => `#${c.Name || c}`)
            : [],
          price: item.tickets?.[0]
            ? `€${parseFloat(item.tickets[0].Amount).toFixed(0)}`
            : this.$t('common.free'),
          priceSub: item.tickets?.[0]?.Type || "",
          status: statusMap[statusObj.status] || "rsvp",
          attendees: [],
          attendeeCount: "",
          saved: false,

          endDate: item.end_date,
          startTime: item.start_time,
          endTime: item.end_time,
          organizer: item.organizer,
          capacity: item.capacity,
          category: item.categories,
          eventType: item.EventType,
          longitude: item.longitude,
          latitude: item.latitude,

          organizerLogos: Array.isArray(item.organizer_logos)
            ? item.organizer_logos.map((logo) => ({
                url:
                  logo.LogoURL ||
                  "https://www.adaptivewfs.com/wp-content/uploads/2020/07/logo-placeholder-image.png",
              }))
            : [],
        };
      });
    },
  },

  async created() {
    if (this.searchTerm) {
      await this.fetchEvents();
    }
  },

  watch: {
    "$route.query.event"(newKeyword) {
      if (newKeyword && newKeyword !== this.searchTerm) {
        this.searchTerm = newKeyword;
        this.fetchEvents();
      }
    },

    searchTerm(newTerm) {
      if (newTerm && newTerm !== this.$route.query.event) {
        this.$router.push({
          name: "search-results",
          query: { event: newTerm },
        });
        this.fetchEvents();
      }
    },
  },

  methods: {
    async fetchEvents() {
      if (!this.searchTerm.trim()) return;

      this.loading = true;

      try {
        const response = await apiService.searchEvents(this.searchTerm.trim());

        if (response?.data) {
          this.events = response.data;
        } else {
          this.events = [];
        }
      } catch (error) {
        this.events = [];
        alert("Failed to load events.");
      } finally {
        this.loading = false;
      }
    },

    getEventStatus(event) {
      const now = new Date();
      const eventDate = new Date(event.date);

      if (isNaN(eventDate)) {
        return { status: "Invalid Date", color: "bg-gray-500" };
      }

      const startTime = event.start_time?.split(":");
      if (!startTime || startTime.length !== 2) {
        return { status: "Invalid Time", color: "bg-gray-500" };
      }

      eventDate.setHours(startTime[0], startTime[1], 0, 0);

      const endDate = new Date(eventDate);
      const endTime = event.end_time?.split(":");

      if (!endTime || endTime.length !== 2) {
        return { status: "Invalid Time", color: "bg-gray-500" };
      }

      endDate.setHours(endTime[0], endTime[1], 0, 0);

      if (now >= eventDate && now <= endDate) {
        return { status: "LIVE", color: "bg-green-500" };
      }

      if (now > endDate) {
        return { status: "ENDED", color: "bg-red-500" };
      }

      if (eventDate - now <= 24 * 60 * 60 * 1000) {
        return { status: "STARTING SOON", color: "bg-yellow-500" };
      }

      return { status: "RSVP", color: "bg-blue-500" };
    },
  },
};
</script>